import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { ClientReadableStream } from 'grpc-web'
import { Decimal } from '@taxfyle/api-internal/shared/types/decimal_pb'
import { Observable } from 'rxjs/internal/Observable'
import { Duration } from 'google-protobuf/google/protobuf/duration_pb'

export interface IDecimal {
  getUnits(): number
  getNanos(): number
}

export interface IDecimalAsObject {
  units: number
  nanos: number
}

const nanoFactor = 1_000_000_000

/**
 * Converts a proto Timestamp to an ISO8601 string date
 * @param timestamp The timestamp to convert either in Timestamp protobuf
 * format or as an object
 */
export function timestampToISO(timestamp?: Timestamp | Timestamp.AsObject) {
  if (!timestamp) {
    return timestamp
  }

  if ('toDate' in timestamp) {
    return timestamp.toDate().toISOString()
  } else {
    return new Date(
      timestamp.seconds * 1000 + timestamp.nanos / 1000000,
    ).toISOString()
  }
}

/**
 * Creates a timestamp from an ISO representation.
 *
 * @param iso
 * @constructor
 */
export function ISOToTimestamp(iso: string) {
  const timestamp = new Timestamp()
  timestamp.fromDate(new Date(iso))
  return timestamp
}

/**
 * Converts a proto custom Decimal type into a Number
 * @param decimal The decimal to convert either in Decimal protobuf format or as
 * an object
 */
export function decimalToNumber(decimal: IDecimal | IDecimalAsObject) {
  if ('getUnits' in decimal) {
    return decimal.getUnits() + decimal.getNanos() / nanoFactor
  } else {
    return decimal.units + decimal.nanos / nanoFactor
  }
}

/**
 * Converts a proto duration into minutes.
 *
 * @param {*} duration
 * @returns
 */
export function durationToMinutes(duration: Duration | Duration.AsObject) {
  if ('getSeconds' in duration) {
    return (duration.getSeconds() + duration.getNanos() / nanoFactor) / 60
  } else {
    return (duration.seconds + duration.nanos / nanoFactor) / 60
  }
}

/**
 * Converts a Number into a proto custom Decimal type
 *
 * @param number The number to convert to a decimal.
 */
export function numberToDecimal(number: number): Decimal {
  const decimal = new Decimal()
  const units = Math.floor(number)
  const nanos = Math.floor((number - units) * nanoFactor)
  decimal.setUnits(units)
  decimal.setNanos(nanos)
  return decimal
}

/**
 * Enables grpc-web dev tools on the specified clients
 */
export function enableDevTools(...clients: any[]) {
  const enableDevTools = (window as any).__GRPCWEB_DEVTOOLS__
  if (enableDevTools) {
    enableDevTools(clients)
  }
}

/**
 * Gets an observable from a streaming call.
 *
 * @param start Factory function to start the streaming call when subscribed to.
 * @param errorOnEnd If `true`, emits an error when the call is ended by the
 *  server. Default is `false`.
 */
export function fromStreamingCall<T>(
  start: () => ClientReadableStream<T>,
  errorOnEnd?: boolean,
): Observable<T> {
  return new Observable<T>((observer) => {
    const streamingCall = start()

    streamingCall.on('data', (response) => {
      observer.next(response)
    })

    if (errorOnEnd) {
      streamingCall.on('end', () => {
        observer.error(new Error('Streaming call ended'))
      })
    }

    streamingCall.on('error', (err) => {
      observer.error(err)
    })

    return () => {
      streamingCall.cancel()
    }
  })
}
